import * as H from 'history';
import * as queryString from 'query-string';
import _ from 'underscore';

import { QueryParameterHelper } from '~src/ts/helpers/QueryParameterHelper';

import { Helpers } from '../../../ts/helpers/Helpers';
import { VizService } from './VizService';

export namespace VizUrl {
  // Returns query parameter value, or null if there is no explicit value.
  // e.g. "?:embed" returns null, ":embed=blah" returns "blah"
  // Using $location.search() directly will give boolean true for keys that
  // are present with no value.
  export function getExplicitLocationSearchValue(routeLocationSearch: string, key: string): string {
    let params = queryString.parse(routeLocationSearch);
    const value = params[key];
    return _.isString(value) ? value : null;
  }

  export function getInstanceId(routeLocationSearch: string): string {
    return getExplicitLocationSearchValue(routeLocationSearch, VizService.InstanceIdKey);
  }

  export function getVizParams(routeLocationSearch: string): _.Dictionary<any> {
    let params: _.Dictionary<boolean | string | string[]> = queryString.parse(routeLocationSearch);

    // tbViz can't handle null values for the url params. $location.search (used in VizService.getVizParams)
    // returns true instead of null. Ex: "?foo" should be ["foo": true]
    params = _.object(_.map(params, (value, key) => {
      if (value === null) {
        return [key, true];
      } else {
        return [key, value];
      }}));
    return _.omit(params, VizService.InstanceIdKey);
  }

  export function getVizPathFromUrlLocation(location: H.Location) {
    // the location.pathname for the view page is "/site/SiteName/views/WorkbookName/SheetName"
    // or (with custom views) "/site/SiteName/views/WorkbookName/SheetName/workgroupadmin@tsi.lan/noiceland"
    // so we need to remove 6 characters for "/site/", plus the site name, and 7 characters for "/views/" to make sure we get
    // the entire path
    const path = location.pathname;
    let indexOfViews = 0;

    if (path.startsWith('/site/')) {
      const pathParts = path.split('/');
      const numCharsInSiteName = pathParts[2].length;
      indexOfViews = 6 + numCharsInSiteName;
    }

    return location.pathname.substr(indexOfViews + 7);
  }

  export function getIsExplicitOriginalVizFromUrl(url: string): boolean {
    // TODO: we should parse a viz object from a url instead of having several of these
    // one-off functions to get individual parts of the url. Can copy angular's url parsing code
    const match = decodeURIComponent(url).match(/[&\?]:original_view=([^&#$]+)/);
    return !!(match && match[1] && Helpers.BooleanQueryParamRegex.test(match[1]));
  }

  /**
   * Updates a query parameter in the hash of the url.
   * @param {string} queryParameterKey - Key to be updated in query param.
   * @param {string} value - Value for the key to be updated.
   */
  export function updateHashQueryParameter(queryParameterKey: string, value: string){
    const urlParts = queryString.parseUrl(window.location.hash);
    urlParts.query[queryParameterKey] = value;
    const updatedUrl = QueryParameterHelper.urlWithParams(urlParts.url, urlParts.query);
    window.location.replace(updatedUrl);
  }

  /**
   * Helper function to remove a given query parameter from the url query string
   * @param {string} queryParameterKey - Key to be removed from the URL.
   */
  export function deleteHashQueryParameter(queryParameterKey: string){
    const urlParts = queryString.parseUrl(window.location.hash);
    delete urlParts.query[queryParameterKey];
    const updatedUrl = QueryParameterHelper.urlWithParams(urlParts.url, urlParts.query);
    window.location.replace(updatedUrl);
  }
}
